import { useEffect, useRef, useState, useCallback } from "react";
import ReviewCard from "./ReviewCard";
import "./reviewsCarousel.scss";

export default function ReviewsCarousel({ reviews }) {
    const containerRef = useRef(null);
    const trackRef = useRef(null);

    const [index, setIndex] = useState(0);
    const [cardWidth, setCardWidth] = useState(0);
    const [gap, setGap] = useState(20);
    const [transitionEnabled, setTransitionEnabled] = useState(true);
    const [isDragging, setIsDragging] = useState(false);

    const startX = useRef(0);
    const lastX = useRef(0);

    // Tripled slides for seamless infinite loop
    const slides = [...reviews, ...reviews, ...reviews];
    const middleIndex = reviews.length;

    /* ----------------------------------------
       READ REAL CARD WIDTH + REAL GAP FROM CSS
    ----------------------------------------- */
    const measureSizes = useCallback(() => {
        const track = trackRef.current;
        if (!track) return;

        // Measure first card width
        const firstCard = track.querySelector(".reviewCard");
        if (firstCard) {
            const width = firstCard.getBoundingClientRect().width;
            setCardWidth(width);
        }

        // Measure gap from CSS
        const style = window.getComputedStyle(track);
        const gapVal = parseFloat(style.gap || style.columnGap || 20);
        setGap(gapVal);
    }, []);

    // Measure sizes on mount + resize
    useEffect(() => {
        measureSizes();
        window.addEventListener("resize", measureSizes);
        return () => window.removeEventListener("resize", measureSizes);
    }, [measureSizes]);

    /* ----------------------------------------
       POSITION TRACK ON INDEX CHANGE
    ----------------------------------------- */
    useEffect(() => {
        if (!trackRef.current || !cardWidth) return;

        const distance = index * (cardWidth + gap);

        trackRef.current.style.transition = transitionEnabled
            ? "transform 0.45s ease"
            : "none";

        trackRef.current.style.transform = `translateX(${-distance}px)`;
    }, [index, cardWidth, gap, transitionEnabled]);

    /* ----------------------------------------
       INFINITE LOOP SNAP
    ----------------------------------------- */
    const onTransitionEnd = () => {
        if (index >= middleIndex * 2) {
            setTransitionEnabled(false);
            setIndex(middleIndex);
        } else if (index < middleIndex) {
            setTransitionEnabled(false);
            setIndex(middleIndex * 2 - 1);
        }
    };

    /* ----------------------------------------
       NEXT / PREV
    ----------------------------------------- */
    const next = useCallback(
        () => {
            setTransitionEnabled(true);
            setIndex((i) => i + 1);
        },
        []
    );

    const prev = useCallback(
        () => {
            setTransitionEnabled(true);
            setIndex((i) => i - 1);
        },
        []
    );

    /* ----------------------------------------
       AUTOPLAY (PAUSES WHEN DRAGGING)
    ----------------------------------------- */
    useEffect(() => {
        if (isDragging) return;
        const timer = setInterval(next, 3000);
        return () => clearInterval(timer);
    }, [isDragging, next]);

    /* ----------------------------------------
       DRAG HANDLERS (WITH PHYSICS)
    ----------------------------------------- */
    const onDragStart = (e) => {
        setIsDragging(true);
        setTransitionEnabled(false);

        startX.current = e.touches ? e.touches[0].clientX : e.clientX;
        lastX.current = startX.current;
    };

    const onDragMove = (e) => {
        if (!isDragging) return;

        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        lastX.current = clientX;

        const dragDelta = clientX - startX.current;
        const baseX = -(index * (cardWidth + gap));

        trackRef.current.style.transform = `translateX(${baseX + dragDelta}px)`;
    };

    const onDragEnd = () => {
        if (!isDragging) return;
        setIsDragging(false);

        const velocity = lastX.current - startX.current;

        if (velocity < -40) next();
        else if (velocity > 40) prev();
        else {
            setTransitionEnabled(true);
            setIndex((i) => i); // Snap back
        }
    };

    return (
        <div className="reviews-wrapper expanded">
            <div className="fade-left"></div>
            <div className="fade-right"></div>

            <div
                className="reviews-container fullBleed"
                ref={containerRef}
                onTouchStart={onDragStart}
                onTouchMove={onDragMove}
                onTouchEnd={onDragEnd}
                onMouseDown={onDragStart}
                onMouseMove={(e) => isDragging && onDragMove(e)}
                onMouseUp={onDragEnd}
                onMouseLeave={onDragEnd}
            >
                <div
                    className="reviews-track"
                    ref={trackRef}
                    onTransitionEnd={onTransitionEnd}
                >
                    {slides.map((review, i) => (
                        <ReviewCard key={i} review={review} />
                    ))}
                </div>
            </div>
        </div>
    );
}
